import { toast } from 'react-toastify';

import { ID, Response } from 'app/components/Table/core/types';

import { Rack } from 'app/modules/inventory/racks/core/type';

import { getResponseError } from 'app/helpers/errors';

import { API_URL } from 'app/constants/environments';

import { StatusItem } from './constants';
import {
  Item,
  ItemsQueryResponse,
  SmartWmsSyncSummary,
  StockValueSetting,
  StockValueSettingQueryResponse,
} from './type';
import axios, { AxiosResponse } from 'axios';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const ITEM_URL = `${API_URL}/api`;
const getItems = (query: string): Promise<ItemsQueryResponse> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .get(`${ITEM_URL}/orgs/${organizationId}/items?${query}`)
    .then(
      (
        d: AxiosResponse<{
          data: {
            items: Array<Item>;
            total: number;
          };
        }>,
      ) => {
        return {
          data: d.data.data,
        };
      },
    )
    .catch((error) => {
      throw error;
    });
};

const getItem = (query: string, idItem: ID): Promise<Item | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .get(`${ITEM_URL}/orgs/${organizationId}/items/${idItem}?${query}`)
    .then((response: AxiosResponse<Response<Item>>) => response.data)
    .then((response: Response<Item>) => response.data);
};
const getItemTable = (
  query: string,
  idItem: ID,
  organizationId: number | undefined,
): Promise<StockValueSettingQueryResponse | undefined> => {
  return axios.get(`${ITEM_URL}/orgs/${organizationId}/items/${idItem}?${query}`).then(
    (
      d: AxiosResponse<{
        data: {
          stockValue: {
            items: Array<StockValueSetting>;
            total: number;
            dataItem: Item;
          };
        };
      }>,
    ) => {
      return {
        data: {
          ...d.data.data.stockValue,
          dataItem: d.data.data as any,
        },
      };
    },
  );
};

const createItem = (request: FormData): Promise<Item | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .post(`${ITEM_URL}/orgs/${organizationId}/items`, request)
    .then((response: AxiosResponse<Response<Item>>) => response.data)
    .then((response: Response<Item>) => response.data);
};

const updateItem = (idItem: ID, request: FormData): Promise<Item | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .patch(`${ITEM_URL}/orgs/${organizationId}/items/${idItem}`, request)
    .then((response: AxiosResponse<Response<Item>>) => response.data)
    .then((response: Response<Item>) => response.data);
};

const deleteItem = async (idItem: ID): Promise<void> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    await axios.delete(`${ITEM_URL}/orgs/${organizationId}/items/${idItem}`);
  } catch (error) {
    console.log(`Error deleting item ${idItem}`);
    throw error;
  }
};

const changeStatusItem = (
  itemId: ID,
  request: { activate: StatusItem },
): Promise<Item | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .patch(`${ITEM_URL}/orgs/${organizationId}/items/${itemId}/activate`, request)
    .then((response: AxiosResponse<Response<Item>>) => response.data)
    .then((response: Response<Item>) => response.data);
};

const patchStockValueItem = (
  itemId: ID,
  request: {
    wardId: number;
    restockValue: number;
    alarmValue: number;
    parValue: number;
  },
  onSuccess: () => void,
  onError: () => void,
  onFinally: () => void,
): Promise<void> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .patch(`${ITEM_URL}/orgs/${organizationId}/items/${itemId}/stock-value`, request)
    .then((response: AxiosResponse<Response<StockValueSetting>>) => {
      onSuccess();
    })
    .catch((error) => {
      getResponseError(error);
      onError();
    })
    .finally(onFinally);
};

const getBinsByItemId = (query: string, itemId: ID) => {
  console.log('query', query);
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios.get(`${API_URL}/api/orgs/${organizationId}/bins/by-item/${itemId}?${query}`).then(
    (
      d: AxiosResponse<{
        data: {
          items: Array<any>;
          total: number;
        };
      }>,
    ) => {
      return {
        data: d.data.data,
      };
    },
  );
};

const assignItemToMultipleBins = async (payload: {
  itemId: number | undefined;
  binIds: (number | null)[];
}) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.post<{ success: boolean }>(
      `${API_URL}/api/orgs/${organizationId}/bins/assign-to-multiple-bins`,
      payload,
    );

    if (response.data.success) {
      toast.success('Item assigned to bins successfully');
    }
  } catch (error) {
    getResponseError(error);
    throw error;
  }
};

const getRacksByWardId = async (wardId: ID) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.get<AxiosResponse<Response<Rack>>>(
      `${API_URL}/api/orgs/${organizationId}/racks-v2/ward/${wardId}/racks`,
    );
    return response.data.data;
  } catch (error) {
    getResponseError(error);
    throw error;
  }
};

const getBinsByRack = async (rackId: ID) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.get(`${API_URL}/api/orgs/${organizationId}/bins/rack/${rackId}`);
    return response.data.data;
  } catch (error) {
    getResponseError(error);
    throw error;
  }
};

const syncItemsFromSmartWms = async (): Promise<SmartWmsSyncSummary> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const response = await axios.post<Response<SmartWmsSyncSummary>>(
      `${ITEM_URL}/orgs/${organizationId}/items/sync/smartwms`,
    );
    return response.data.data as SmartWmsSyncSummary;
  } catch (error) {
    getResponseError(error);
    throw error;
  }
};

const getAssginedItem = async (query?: string, itemId?: ID) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;
  const queryString = query ? `?${query}` : '';

  return axios
    .get(`${ITEM_URL}/orgs/${organizationId}/items/${itemId}/assigned${queryString}`)
    .then(
      (
        d: AxiosResponse<{
          data: {
            items: Array<Item>;
            total: number;
          };
        }>,
      ) => {
        return {
          data: d.data.data,
        };
      },
    )
    .catch((error) => {
      throw error;
    });
};

export {
  assignItemToMultipleBins,
  changeStatusItem,
  createItem,
  deleteItem,
  getBinsByItemId,
  getBinsByRack,
  getItem,
  getItems,
  getItemTable,
  getAssginedItem,
  getRacksByWardId,
  patchStockValueItem,
  syncItemsFromSmartWms,
  updateItem,
};
